import { HttpClient, HttpParams } from '@angular/common/http';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { Listing } from '../../models/listing';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ListingService extends ResourceService<Listing> {
  /**
   * @constructor
   * Creates an instance of the ListingService
   *
   * @param {HttpClient} http Injected instance of HttpClient
   * @param {CacheService} cache Injected instance of CacheService
   */
  constructor(protected http: HttpClient, protected cache: CacheService) {
    super(http, cache, 'listings');
  }

  /**
   * Sends a GET request to the /listings/recommended endpoint
   * @remarks
   * to return copies of the recommended listings on the server
   *
   * @param {HttpParams} queryOptions Object to filter the resources by on the server-side
   * @param {boolean} raw: Optional flag that determine what is returned.
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  recommendedListings(queryOptions?: HttpParams, raw?: boolean) {
    return this.http
      .get<any>(`${this.endpoint}/recommended`, { params: queryOptions })
      .pipe(
        map((resp: any) => {
          resp.data.listings = this.convertData(resp.data.listings);
          if (!raw) {
            return resp.data.listings;
          }
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /listings/latest endpoint
   * @remarks
   * to return copies of lates listings on the server
   *
   * @param {HttpParams} queryOptions Object to filter the resources by on the server-side
   * @param {boolean} raw Optional Boolean params to filter the resources by on the server-side
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  latestListings(queryOptions?: HttpParams, raw?: boolean) {
    return this.http
      .get<any>(`${this.endpoint}/latest`, { params: queryOptions })
      .pipe(
        map((resp: any) => {
          resp.data.listings = this.convertData(resp.data.listings);
          if (!raw) {
            return resp.data.listings;
          }
          return resp;
        })
      );
  }

  allow_or_reject(item: { id: string; listed: boolean }) {
    return this.http.put(`${this.endpoint}/${item.id}/visibility`, item).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}/${item.id}`);
        this.cache.remove(`${this.endpoint}`);
        return resp.data.listing;
      })
    );
  }

  bulk_allow_or_reject(data: { ids: string[]; listed: boolean }) {
    return this.http.put(`${this.endpoint}/visibility`, data).pipe(
      map((resp: any) => {
        data.ids.forEach((id) => this.cache.remove(`${this.endpoint}/${id}`));
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a PUT request to the /listings/:id/zoopla-lists endpoint
   * @remarks
   * to add or remove property from Zoopla
   *
   * @param {id: string, list_on_zoopla: boolean} item Object to perform the operation on the selected resources on the server-side
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  send_or_remove_from_zoopla(item: { id: string; list_on_zoopla: boolean }) {
    return this.http.put(`${this.endpoint}/${item.id}/zoopla-lists`, item).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}/${item.id}`);
        this.cache.remove(`${this.endpoint}`);
        return resp.data.listing;
      })
    );
  }

  /**
   * Sends a PUT request to the /listings/zoopla-lists endpoint
   * @remarks
   * to add or remove properties to/from Zoopla listings
   *
   * @param {list_on_zoopla: boolean, ids: string[]} data Object to perform the operation on the selected resources on the server-side
   * @returns {Observable} Returns an observable
   */
  bulk_send_or_remove_from_zoopla(data: {
    ids: string[];
    list_on_zoopla: boolean;
  }) {
    return this.http.put(`${this.endpoint}/zoopla-lists`, data).pipe(
      map((resp: any) => {
        data.ids.forEach((id) => this.cache.remove(`${this.endpoint}/${id}`));
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /listings/search endpoint
   * @remarks
   * to save property search that currently doesnt exist on the server
   *
   * @param {any} queryOptions Object to filter the resources by on the server-side
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  saveSearch(queryOptions: any) {
    return this.http
      .post(`${this.endpoint}/search`, this.serializer.toJson(queryOptions))
      .pipe(
        map((resp: any) => {
          resp.data[this.collections] = this.convertData(
            resp.data[this.collections]
          );
          return resp;
        })
      );
  }

  /**
   * Sends a PUT request to the /listings/likes/:id endpoint
   * @remarks
   * to add a property to list of favourites which can be seen on favorites page
   *
   * @param {any} payload Object to toggle to add property to favorite list, ommit toggle:true to unfavorite property
   *
   * @param {string} id Id of property to be liked or unliked
   *
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  toggleFavoriteProperty(payload: any, id: string) {
    return this.http
      .put(`${this.endpoint}/${id}/likes`, this.serializer.toJson(payload))
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}/${id}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /listings/likes endpoint
   * @remarks
   * to list of properties a single user liked
   *
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getFavoriteProperties(queryOptions?: HttpParams, raw?: boolean) {
    return this.http
      .get(`${this.endpoint}/likes`, { params: queryOptions })
      .pipe(
        map((resp: any) => {
          resp.data.listings = this.convertData(resp.data.listings);
          if (!raw) {
            return resp.data.listings;
          }
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /listings/priceline-reviews endpoint
   * @remarks
   * to get reviews from priceline properties
   *
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getPropertyReview(queryOptions?: any, raw?: boolean) {
    return this.http
      .get(`${this.endpoint}/priceline-reviews`, { params: queryOptions })
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  getPricelinePropertyCOntract(queryOptions: any) {
    return this.http
      .get(`${this.endpoint}/priceline-contracts`, { params: queryOptions })
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  // Location Endpoints
  /**
   * Update an existing location by ID.
   *
   * @param id - The ID of the location to be updated.
   * @param data - The updated data for the location.
   * @returns An observable that emits the result of the update operation.
   */
  updateLocation(id: string, data: any) {
    // const url = `${this.endpoint}/${id}`;
    return this.http.put(`location/${id}`, data).pipe(
      map((resp: any) => {
        this.cache.remove(`location`);
        this.cache.remove(`location`);
        return resp;
      })
    );
  }

  /**
   * Create a new location.
   *
   * @param data - The data for the new location.
   * @returns An observable that emits the result of the creation operation.
   */
  createLocation(data: any) {
    return this.http.post('location', data).pipe(
      map((resp: any) => {
        this.cache.remove(`location`);
        this.cache.remove(`location`);
        return resp;
      })
    );
  }

  /**
   * Get a list of saved locations based on a specified location name.
   *
   * @param location - The location name to filter saved locations.
   * @returns An observable that emits the list of saved locations.
   */
  getSavedLocations(params?: any) {
    // const url = `${this.endpoint}?location=${location}`;
    // return this.http.get(url);
    return this.http.get(`location`, { params }).pipe(
      map((resp: any) => {
        this.cache.remove(`location`);
        this.cache.remove(`location`);
        return resp;
      })
    );
  }

  /**
   * Get details of a location by ID and location name.
   *
   * @param id - The ID of the location to retrieve details.
   * @param location - The location name to filter the result.
   * @returns An observable that emits the details of the specified location.
   */
  getLocationById(id: string, location: string) {
    return this.http.get(`location/${id}?location=${location}`).pipe(
      map((resp: any) => {
        this.cache.remove(`location`);
        this.cache.remove(`location`);
        return resp;
      })
    );
  }

  /**
   * Delete a location.
   *
   * @param id - The ID of the location to delete.
   * @returns An observable that emits the details of the specified location.
   */
  deleteLocation(id: string) {
    return this.http.delete(`location/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`location`);
        this.cache.remove(`location`);
        return resp;
      })
    );
  }
  /**
   * Get preview of a the listing with @id
   *
   * @param id - The ID of the listing to retrieve it's details.
   * @returns An observable that emits the details of the specified listing.
   */
  getPreviewListingById(id: string) {
    return this.http.get(`${this.endpoint}/preview/${id}}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}/preview/${id}}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }


  fetchListingViaAutocomplete(payload: any) {
    return this.http.get(`${this.endpoint}/search/autocomplete`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}/search/autocomplete`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  getRatehawkOrderInfo(payload?: any) {
    return this.http.request('GET', `${this.endpoint}/rh_listing/order/info`, { body: payload }).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }
}
